import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { useOrganizationSecrets } from '@/hooks/useSecrets';
import { useToast } from '@/hooks/useToast';
import { useProjects } from '@/hooks/useProjects';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader } from '@/components/ui/loader';
import { CreateSecretModal } from '@/components/secrets/CreateSecretModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { useTranslation } from '@/hooks/useTranslation';
import { trpcClient } from '@/router';
import {
  PlusIcon,
  DownloadIcon,
  UploadIcon,
  EyeIcon,
  EyeOffIcon,
  CopyIcon,
  EditIcon,
  MoreHorizontalIcon,
  BuildingIcon,
  HomeIcon,
  Copy,
  ArchiveIcon,
  TrashIcon
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { ApiSecret, SecretsResponse, ApiProject, ProjectsResponse, ApiEnvironment, EnvironmentsResponse } from '@envase/api/client';

export const Route = createFileRoute('/_authenticated/org/$orgId/secrets')({
  component: OrganizationSecretsPage,
});

function OrganizationSecretsPage() {
  const { orgId } = Route.useParams();
  const { t } = useTranslation();
  const { organization } = useOrganization(orgId);
  const [searchQuery, setSearchQuery] = useState('');
  const [revealedSecrets, setRevealedSecrets] = useState<Set<string>>(new Set());
  const [createSecretOpen, setCreateSecretOpen] = useState(false);
  const [duplicatingSecret, setDuplicatingSecret] = useState<ApiSecret | null>(null);
  const [editingSecret, setEditingSecret] = useState<ApiSecret | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const { data: secretsData, isLoading, refetch } = useOrganizationSecrets(orgId);
  const { data: projectsData } = useProjects();
  const toast = useToast();

  const secrets: ApiSecret[] = (secretsData as SecretsResponse | undefined)?.secrets || [];
  const allProjects: ApiProject[] = ((projectsData as ProjectsResponse | undefined)?.projects || []).filter(
    (p: ApiProject) => p.organizationId === organization?.id
  );

  // Filtro
  const filteredSecrets = secrets.filter((secret: ApiSecret) =>
    secret.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (secret.description?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );
  // Agrupado por proyecto
  const secretsByProject = filteredSecrets.reduce((acc, sec) => {
    const projectName = sec.projectName || t('orgEnvironments.unknownProject');
    if (!acc[projectName]) {
      acc[projectName] = [];
    }
    acc[projectName].push(sec);
    return acc;
  }, {} as Record<string, ApiSecret[]>);

  const handleReveal = (secretId: string) => {
    setRevealedSecrets(prev => {
      const newSet = new Set(prev);
      newSet.has(secretId) ? newSet.delete(secretId) : newSet.add(secretId);
      return newSet;
    });
  };
  const handleCopy = async (value: string | null) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleExportSecrets = () => {
    const exportData = secrets.map(secret => ({
      key: secret.key,
      value: secret.value || '',
      description: secret.description || '',
      project: secret.projectName || '',
      environment: secret.environmentName || '',
    }));

    // Export as JSON
    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `secrets-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportSecrets = async (file: File) => {
    try {
      const text = await file.text();
      let importData: Array<{
        key: string;
        value: string;
        description?: string;
        project?: string;
        environment?: string;
      }> = [];

      if (file.name.endsWith('.json')) {
        importData = JSON.parse(text);
      } else if (file.name.endsWith('.csv')) {
        // Parse CSV
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim());
        importData = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim());
          const obj: Record<string, string> = {};
          headers.forEach((header, i) => {
            obj[header] = values[i] || '';
          });
          return obj as {
            key: string;
            value: string;
            description?: string;
            project?: string;
            environment?: string;
          };
        });
      }

      // Show confirmation dialog with count
      const confirmed = window.confirm(
        `Import ${importData.length} secrets? This will create new secrets for each entry.`
      );
      
      if (!confirmed) return;

      setIsImporting(true);

      // Get all projects and environments for mapping
      const projectMap = new Map<string, ApiProject>();
      const environmentMap = new Map<string, { projectId: string; environment: ApiEnvironment }>();

      for (const project of allProjects) {
        projectMap.set(project.name, project);
        projectMap.set(project.slug, project);

        // Fetch environments for each project
        try {
          const envsData = await trpcClient.environments.getByProject.query({ projectId: project.id });
          const envs = (envsData as EnvironmentsResponse | undefined)?.environments || [];
          for (const env of envs) {
            environmentMap.set(`${project.name}:${env.name}`, { projectId: project.id, environment: env });
            environmentMap.set(`${project.slug}:${env.slug}`, { projectId: project.id, environment: env });
          }
        } catch (error) {
          console.error(`Failed to fetch environments for project ${project.id}:`, error);
        }
      }

      // Process each secret
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      for (const secretData of importData) {
        try {
          // Find project by name or slug
          let project: ApiProject | undefined = projectMap.get(secretData.project || '');
          
          // If no project specified or not found, use first project as fallback
          if (!project && allProjects.length > 0) {
            project = allProjects[0];
          }

          if (!project) {
            errors.push(`Secret "${secretData.key}": No project found. Skipping.`);
            errorCount++;
            continue;
          }

          // Find environment if specified
          let environmentId: string | undefined;
          if (secretData.environment) {
            const envKey = `${project.name}:${secretData.environment}`;
            const envData = environmentMap.get(envKey) || environmentMap.get(`${project.slug}:${secretData.environment}`);
            
            if (envData && envData.projectId === project.id) {
              environmentId = envData.environment.id;
            } else {
              errors.push(`Secret "${secretData.key}": Environment "${secretData.environment}" not found in project "${project.name}". Creating as project-level secret.`);
            }
          }

          // Create secret
          await trpcClient.secrets.create.mutate({
            projectId: project.id,
            key: secretData.key,
            value: secretData.value,
            description: secretData.description || undefined,
            environmentId: environmentId,
            scope: environmentId ? 'environment' : 'project',
          });

          successCount++;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`Secret "${secretData.key}": ${errorMessage}`);
          errorCount++;
        }
      }

      setIsImporting(false);
      
      // Refresh secrets list
      await refetch();

      // Show results
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} secret${successCount > 1 ? 's' : ''}`);
      }
      
      if (errorCount > 0) {
        const errorSummary = errors.slice(0, 5).join('\n');
        const moreErrors = errors.length > 5 ? `\n... and ${errors.length - 5} more errors` : '';
        toast.error(`Failed to import ${errorCount} secret${errorCount > 1 ? 's' : ''}: ${errorSummary}${moreErrors}`);
      }

      if (successCount === 0 && errorCount === 0) {
        toast.warning('No secrets were imported.');
      }
      
    } catch (error) {
      setIsImporting(false);
      toast.error(`Failed to import secrets: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDuplicateSecret = (secret: ApiSecret) => {
    setDuplicatingSecret(secret);
    setCreateSecretOpen(true);
  };

  const handleArchiveSecret = async (secret: ApiSecret) => {
    const confirmed = window.confirm(
      `Archive secret "${secret.key}"? It will be hidden but not deleted.`
    );
    
    if (!confirmed) return;

    try {
      // Use trpcClient directly
      const { trpcClient } = await import('@/router');
      await trpcClient.secrets.delete.mutate({
        secretId: secret.id,
      });
      
      toast.success(`Secret "${secret.key}" archived successfully`);
      refetch();
    } catch (error) {
      toast.error(`Failed to archive secret: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="pt-16 flex justify-center"><Loader variant="spinner" size="lg" color="accent" text="Loading secrets..." /></div>
    );
  }

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: 'Secrets',
      to: `/org/${organization?.slug || orgId}/secrets`,
    },
  ];

  return (
    <div className="space-y-8">
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={t('orgSecrets.title')}
        action={
          <Button
            asChild
            className="bg-primary text-white hover:bg-primary/90 py-2 px-4 rounded"
          >
            <a href="/dashboard" tabIndex={0}>
              <BuildingIcon className="w-5 h-5 mr-2" />
              {t('orgEnvironments.selectOrg')}
            </a>
          </Button>
        }
      />

      <div>
        <div className="flex items-center justify-between mb-4 gap-4">
          <Input
            type="search"
            placeholder={t('orgSecrets.searchPlaceholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="max-w-lg"
          />
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleExportSecrets} 
              variant="outline"
              title="Export secrets to CSV/JSON"
            >
              <DownloadIcon className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button 
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.csv,.json';
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) handleImportSecrets(file);
                };
                input.click();
              }}
              variant="outline"
              title="Import secrets from CSV/JSON"
              disabled={isImporting}
            >
              <UploadIcon className="w-4 h-4 mr-2" />
              {isImporting ? 'Importing...' : 'Import'}
            </Button>
            <Button onClick={() => setCreateSecretOpen(true)} variant="default">
              <PlusIcon className="w-4 h-4 mr-2" />
              {t('orgSecrets.create')}
            </Button>
          </div>
        </div>

        {/* Tabla agrupada */}
        {Object.keys(secretsByProject).length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('orgSecrets.colKey')}</TableHead>
                <TableHead>{t('orgSecrets.colValue')}</TableHead>
                <TableHead>{t('orgSecrets.colDescription')}</TableHead>
                <TableHead>{t('orgSecrets.colProject')}</TableHead>
                <TableHead className="text-right">{t('orgSecrets.colActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(secretsByProject).map(([project, secretList]) => [
                <TableRow key={`prj-row-${project}`}>
                  <TableCell colSpan={5} className="bg-gray-50 font-semibold text-gray-700">{project}</TableCell>
                </TableRow>,
                ...secretList.map((secret: ApiSecret) => (
                  <TableRow key={secret.id}>
                    <TableCell className="font-mono text-sm max-w-[180px] truncate" title={secret.key}>{secret.key}</TableCell>
                    <TableCell className="relative">
                      <div className="flex items-center gap-2">
                        <span className={revealedSecrets.has(secret.id) ? '' : 'blur-sm select-none'}>{secret.value}</span>
                        <Button variant="ghost" size="icon" onClick={() => handleReveal(secret.id)} title={revealedSecrets.has(secret.id) ? t('orgSecrets.hide') : t('orgSecrets.reveal')}>
                          {revealedSecrets.has(secret.id) ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleCopy(secret.value)} title={t('orgSecrets.copy')}>
                          <CopyIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm max-w-[220px] truncate" title={secret.description || undefined}>{secret.description}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{secret.projectName || t('orgEnvironments.unknownProject')}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm"><MoreHorizontalIcon className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleReveal(secret.id)}>
                            {revealedSecrets.has(secret.id) ? <EyeOffIcon className="w-4 h-4 mr-2" /> : <EyeIcon className="w-4 h-4 mr-2" />}
                            {revealedSecrets.has(secret.id) ? t('orgSecrets.hide') : t('orgSecrets.reveal')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCopy(secret.value)}>
                            <CopyIcon className="w-4 h-4 mr-2" />{t('orgSecrets.copy')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditingSecret(secret)}>
                            <EditIcon className="w-4 h-4 mr-2" />{t('orgSecrets.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicateSecret(secret)}>
                            <Copy className="w-4 h-4 mr-2" />Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleArchiveSecret(secret)} className="text-orange-600">
                            <ArchiveIcon className="w-4 h-4 mr-2" />Archive
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600" onClick={() => handleArchiveSecret(secret)}>
                            <TrashIcon className="w-4 h-4 mr-2" />{t('orgSecrets.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ])}
            </TableBody>
          </Table>
        ) : (
          <div className="bg-white p-12 rounded-lg shadow text-center">
            <h3 className="text-lg font-semibold mb-2">{t('orgSecrets.noSecrets')}</h3>
            <p className="text-gray-600 mb-6">{t('orgSecrets.noSecretsHint')}</p>
            <Button onClick={() => setCreateSecretOpen(true)}><PlusIcon className="w-4 h-4 mr-2" />{t('orgSecrets.create')}</Button>
          </div>
        )}
      </div>

      {/* Create Secret Modal */}
      <CreateSecretModal
        open={createSecretOpen}
        onOpenChange={(open) => {
          setCreateSecretOpen(open);
          if (!open) {
            setDuplicatingSecret(null);
          }
        }}
        onSuccess={() => {
          refetch();
          setCreateSecretOpen(false);
          setDuplicatingSecret(null);
        }}
        organizationId={orgId}
        initialSecret={duplicatingSecret ? {
          key: `${duplicatingSecret.key}_copy`,
          value: duplicatingSecret.value || '',
          description: duplicatingSecret.description || '',
          projectId: duplicatingSecret.projectId,
          environmentId: duplicatingSecret.environmentId || undefined,
        } : undefined}
      />

      {/* Edit Secret Modal - Navigate to project secrets page */}
      {editingSecret && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Secret</h3>
            <p className="text-gray-600 mb-4">
              To edit this secret, please navigate to the project's secrets page.
            </p>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setEditingSecret(null)}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  // Navigate to project secrets page
                  window.location.href = `/org/${orgId}/projects/${editingSecret.projectId}/secrets`;
                }}
              >
                Go to Project Secrets
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

